import { Fragment, useState } from 'react';
import { FolderArchive } from 'lucide-react';
import { useQuery } from '@apollo/client/react';
import { toast } from 'sonner';

import { DialogTitle, DialogDescription, DialogHeader } from '@/components/ui/dialog';
import { useAddDeposit } from 'src/context/deposit';
import { Separator } from '@/components/ui/separator';
import { cn } from 'src/lib/utils';
import { RepositorySelector, RepoUploader, type RepoUploadFailureResult } from 'src/widgets/AssetUpload';
import { AssetTypeEnum } from 'src/graphql/schema';
import { AssetsDepositSourceDocument } from 'src/graphql/actions/asset/assetsDepositSource.query';
import { GetAssetsCountDocument } from 'src/graphql/actions/asset/getAssetsCount.query';
import useRepoUploads from 'src/hooks/use-repo-uploads';
import useRepoVerification from 'src/hooks/use-repo-verification';

const RepoUploadContainer = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string>('');
  const [repoName, setRepoName] = useState<string>('');
  const {
    ui: { selectedEscrowId, setInitialData, getBack, setStep },
    refetch,
    contentType,
  } = useAddDeposit();

  const {
    data: assetsCountData,
    loading: assetsCountLoading,
    refetch: refetchAssetsCount,
  } = useQuery(GetAssetsCountDocument, {
    fetchPolicy: 'network-only',
    variables: { softwareEscrowId: selectedEscrowId, type: AssetTypeEnum.RepoUpload },
    skip: !selectedEscrowId,
  });

  const {
    data: assetsSourceData,
    loading: assetsSourceLoading,
    refetch: refetchAssetsSource,
  } = useQuery(AssetsDepositSourceDocument, {
    fetchPolicy: 'network-only',
    variables: {
      softwareEscrowId: selectedEscrowId,
      type: AssetTypeEnum.RepoUpload,
      perPage: assetsCountData?.assets?.nodesCount,
    },
    skip: assetsCountLoading || !selectedEscrowId,
  });

  const repoOptions =
    assetsSourceData?.assetsSource?.nodes
      .map((asset) => ({
        value: asset.id ?? '',
        label: asset.depositSource ?? '',
      }))
      .filter((option) => option.value) ?? [];

  const handleReset = () => {
    setInitialData();
    refetch?.();
  };

  const handleRepositorySelectorRefetch = async () => {
    await refetchAssetsCount();
    await refetchAssetsSource();
  };

  const handleVerificationComplete = () => {
    setIsUploading(false);
    handleReset();
    toast.success('Asset added!', { description: 'Your asset has been added to this escrow.' });
  };

  const handleVerificationFailure = () => {
    setIsUploading(false);
    setStep('repo-verification-failed');
  };

  const { isVerifying, startVerification, stopVerification } = useRepoVerification(
    handleVerificationComplete,
    handleVerificationFailure,
  );

  const [repoUploadFiles, repoCancelUpload, , , repoUploadProgress] = useRepoUploads(selectedEscrowId);

  const handleUploadFailure = (result: RepoUploadFailureResult) => {
    if (result.status === 'canceled') {
      toast.error('Upload canceled');
    } else {
      toast.error(result.message);
    }
  };

  const handleDiscard = () => {
    setSelectedAssetId('');
    setRepoName('');
  };

  return (
    <Fragment>
      <DialogHeader className={cn('gap-1.5 relative', !isUploading && 'pl-13')}>
        {!isUploading && <FolderArchive className="size-9 absolute left-0 top-[50%] -translate-y-1/2" />}
        <DialogTitle className="text-xl">{isUploading ? 'Deposit in progress' : 'Git repository upload'}</DialogTitle>
        <DialogDescription>
          {isUploading
            ? 'Please wait for the upload to complete.'
            : 'Manually upload a Git repository as a ZIP file. Add a new repo or upload a new version of an existing one.'}
        </DialogDescription>
      </DialogHeader>
      {!isUploading && <Separator />}
      {!isUploading && selectedEscrowId && (
        <div className="px-6">
          <RepositorySelector
            assetsCount={assetsCountData?.assets?.nodesCount}
            options={repoOptions}
            loading={assetsCountLoading || assetsSourceLoading}
            refetch={handleRepositorySelectorRefetch}
            value={selectedAssetId || undefined}
            repoName={repoName || undefined}
            onValueChange={(val) => {
              setSelectedAssetId(val);
              setRepoName('');
            }}
            onCreateAsset={(name) => {
              setRepoName(name);
              setSelectedAssetId('');
            }}
          />
        </div>
      )}
      <RepoUploader
        isUploading={isUploading}
        setIsUploading={setIsUploading}
        back={getBack}
        contentType={contentType!}
        accept={{ 'application/zip': ['.zip'] }}
        repoName={repoName || undefined}
        assetId={selectedAssetId || undefined}
        onDiscard={handleDiscard}
        uploadFiles={repoUploadFiles}
        cancelUpload={repoCancelUpload}
        uploadProgress={repoUploadProgress}
        isVerifying={isVerifying}
        startVerification={startVerification}
        stopVerification={stopVerification}
        onUploadFailure={handleUploadFailure}
        onFileRejected={toast.error}
      />
    </Fragment>
  );
};

export default RepoUploadContainer;
